/**
 * Custom hook to track screen loading performance
 * Tracks both initial page load AND data reloads (when filters change)
 */

import { useEffect, useRef } from 'react';
import performanceTracker from 'services/PerformanceTracking';

/**
 * Track when screen loading starts and completes
 * @param {string} screenId - Screen identifier
 * @param {boolean} isLoading - Whether screen is currently loading
 * @param {Object} metadata - Additional metadata to send with timing
 */
function useScreenLoadingTrackingHook(screenId, isLoading, metadata = {}) {
  const wasLoadingRef = useRef(isLoading); // Initialize with actual loading state
  const metadataRef = useRef(metadata); // Store latest metadata
  
  // Update metadata ref on every render (doesn't trigger re-renders)
  metadataRef.current = metadata;

  useEffect(() => {
    // Start tracking when loading begins (false → true)
    // This captures both navigation AND data reloads (e.g., changing period/channel)
    if (!wasLoadingRef.current && isLoading) {
      performanceTracker.startTracking(screenId);
    }
    
    // End tracking when loading completes (true → false)
    if (wasLoadingRef.current && !isLoading) {
      // Use the current metadata from ref (not from closure)
      performanceTracker.endTracking(screenId, metadataRef.current);
    }
    
    wasLoadingRef.current = isLoading;
  }, [screenId, isLoading]); // ✅ metadata removed from deps to prevent infinite loop
}

export default useScreenLoadingTrackingHook;
